import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Play, CalendarCheck, UserCircle, Sparkle, Receipt, ArrowsOutSimple } from "@phosphor-icons/react";
import ModalCloseButton from "../../ui/ModalCloseButton";
import { HOME_VIDEO, HOME_VIDEO_PLAY_EVENT } from "./homeVideoConfig";
import "./HomeVideo.css";

/*
 * Big centered demo video. Facade pattern: nothing heavy loads until the
 * visitor presses play (YouTube iframe / <video>). Without a configured
 * source, a flat editorial poster is shown instead of a broken player.
 *
 * Mobile (≤768px): playback opens in a full-viewport overlay (portal) with a
 * close button and an "expand" control that requests fullscreen + locks the
 * screen to landscape where the platform allows it (Android/Chrome). iOS
 * ignores both APIs on iframes, so the Vimeo embed also gets `playsinline=0`,
 * which makes iOS open its native fullscreen player (which rotates by itself).
 */

const POSTER_CHIPS = [
  { icon: CalendarCheck, label: "יומן ותזכורות" },
  { icon: UserCircle, label: "תיק מטופל" },
  { icon: Sparkle, label: "סיכומי AI" },
  { icon: Receipt, label: "חשבוניות" },
];

const MOBILE_QUERY = "(max-width: 768px)";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

function VimeoFrame({ className, mobile }) {
  const params = new URLSearchParams({
    autoplay: "1",
    badge: "0",
    autopause: "0",
    dnt: "1",
    transparent: "0",
    player_id: "0",
    app_id: "58479",
  });
  // iOS: no inline playback → native fullscreen player that rotates with the device
  if (mobile) params.set("playsinline", "0");
  return (
    <iframe
      className={className}
      src={`https://player.vimeo.com/video/${HOME_VIDEO.VIMEO_ID}?${params.toString()}`}
      title={HOME_VIDEO.TITLE}
      allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    />
  );
}

function YoutubeFrame({ className, mobile }) {
  return (
    <iframe
      className={className}
      src={`https://www.youtube-nocookie.com/embed/${HOME_VIDEO.YOUTUBE_ID}?autoplay=1&rel=0&modestbranding=1&hl=he${
        mobile ? "&playsinline=0" : "&playsinline=1"
      }`}
      title={HOME_VIDEO.TITLE}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  );
}

function Mp4Player({ className, poster, mobile }) {
  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      className={className}
      src={HOME_VIDEO.MP4_SRC}
      poster={poster || undefined}
      controls
      autoPlay
      playsInline={!mobile}
    />
  );
}

/* Full-viewport mobile player with close + expand/rotate controls */
function MobileOverlay({ children, onClose }) {
  const rootRef = useRef(null);
  const [isFs, setIsFs] = useState(false);

  // Lock page scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Track fullscreen state; release orientation lock on exit
  useEffect(() => {
    const onChange = () => {
      const active = Boolean(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFs(active);
      if (!active) {
        try {
          window.screen?.orientation?.unlock?.();
        } catch {
          /* unsupported */
        }
      }
    };
    document.addEventListener("fullscreenchange", onChange);
    document.addEventListener("webkitfullscreenchange", onChange);
    return () => {
      document.removeEventListener("fullscreenchange", onChange);
      document.removeEventListener("webkitfullscreenchange", onChange);
    };
  }, []);

  // Esc closes
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const expand = useCallback(async () => {
    const el = rootRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        await (document.exitFullscreen?.() || document.webkitExitFullscreen?.());
        return;
      }
      await (el.requestFullscreen?.() || el.webkitRequestFullscreen?.());
      // Best effort: Android/Chrome honors it, iOS does not
      await window.screen?.orientation?.lock?.("landscape");
    } catch {
      /* fullscreen or orientation lock unsupported — overlay still works */
    }
  }, []);

  const close = useCallback(() => {
    try {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen?.() || document.webkitExitFullscreen?.());
      }
    } catch {
      /* ignore */
    }
    onClose();
  }, [onClose]);

  return createPortal(
    <div className="hvid-overlay" ref={rootRef} role="dialog" aria-modal="true" aria-label={HOME_VIDEO.TITLE}>
      <div className="hvid-overlay-bar">
        <ModalCloseButton onClick={close} className="hvid-overlay-close" />
        <button type="button" className="hvid-overlay-expand" onClick={expand}>
          <ArrowsOutSimple size={18} weight="bold" />
          <span>{isFs ? "יציאה ממסך מלא" : "מסך מלא"}</span>
        </button>
      </div>
      <div className="hvid-overlay-stage">{children}</div>
    </div>,
    document.body
  );
}

function HomeVideo() {
  const [playing, setPlaying] = useState(false);
  const [posterSrc, setPosterSrc] = useState(HOME_VIDEO.POSTER_SRC);
  const isMobile = useIsMobile();
  const hasVimeo = Boolean(HOME_VIDEO.VIMEO_ID);
  const hasYoutube = !hasVimeo && Boolean(HOME_VIDEO.YOUTUBE_ID);
  const hasMp4 = !hasVimeo && !hasYoutube && Boolean(HOME_VIDEO.MP4_SRC);
  const hasSource = hasVimeo || hasYoutube || hasMp4;

  // Vimeo thumbnails are hash-based URLs that change whenever the thumbnail is
  // replaced in Vimeo. Refresh from oEmbed so the poster never goes stale; the
  // configured POSTER_SRC shows instantly and stays if the request fails.
  useEffect(() => {
    if (!hasVimeo) return undefined;
    const controller = new AbortController();
    fetch(
      `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(
        `https://vimeo.com/${HOME_VIDEO.VIMEO_ID}`
      )}&width=1280`,
      { signal: controller.signal }
    )
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const url = data?.thumbnail_url;
        if (!url) return;
        // Request a 16:9 1280x720 rendition regardless of the size oEmbed returned
        const hd = url.replace(/_\d+x\d+(\?.*)?$/, "_1280x720");
        setPosterSrc(hd);
      })
      .catch(() => {
        /* keep configured poster; non-critical */
      });
    return () => controller.abort();
  }, [hasVimeo]);

  // Hero CTA ("צפו בסיור") starts playback instead of only scrolling here.
  useEffect(() => {
    if (!hasSource) return undefined;
    const onPlay = () => {
      setPlaying(true);
      document.getElementById("features")?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    window.addEventListener(HOME_VIDEO_PLAY_EVENT, onPlay);
    return () => window.removeEventListener(HOME_VIDEO_PLAY_EVENT, onPlay);
  }, [hasSource]);

  const stop = useCallback(() => setPlaying(false), []);

  const posterStyle = posterSrc
    ? { backgroundImage: `url(${posterSrc})` }
    : hasYoutube
    ? {
        backgroundImage: `url(https://i.ytimg.com/vi/${HOME_VIDEO.YOUTUBE_ID}/maxresdefault.jpg)`,
      }
    : undefined;

  const renderPlayer = (mobile) => {
    const cls = mobile ? "hvid-overlay-media" : "hvid-media";
    if (hasVimeo) return <VimeoFrame className={cls} mobile={mobile} />;
    if (hasYoutube) return <YoutubeFrame className={cls} mobile={mobile} />;
    if (hasMp4) return <Mp4Player className={cls} poster={posterSrc} mobile={mobile} />;
    return null;
  };

  const showInline = playing && !isMobile;
  const showOverlay = playing && isMobile;

  return (
    <div id="features" className={`hvid${showInline ? " hvid--playing" : ""}`} aria-label={HOME_VIDEO.TITLE}>
      {!showInline && hasSource && (
        <div className="hvid-hint" aria-hidden="true">
          <span className="hvid-hint-text">{HOME_VIDEO.HINT}</span>
          <svg className="hvid-hint-arrow" viewBox="0 0 120 110" fill="none">
            {/* hand-drawn loop: starts near the text, curls once, lands on the frame */}
            <path
              className="hvid-hint-path"
              d="M8 10 C 40 2, 86 6, 92 30 C 97 52, 60 62, 56 42 C 53 26, 86 24, 94 48 C 101 70, 84 90, 66 100"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              className="hvid-hint-head"
              d="M58 86 L66 100 L80 96"
              stroke="currentColor"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      <div className="hvid-frame home-bubble--lg">
        {showInline && renderPlayer(false)}

        {!showInline && (
          <div className={`hvid-poster${posterStyle ? " hvid-poster--image" : ""}`} style={posterStyle}>
            {!posterStyle && (
              <div className="hvid-poster-art" aria-hidden="true">
                <div className="hvid-art-bar">
                  <span className="hvid-art-dot" />
                  <span className="hvid-art-dot" />
                  <span className="hvid-art-dot" />
                </div>
                <div className="hvid-art-grid">
                  {POSTER_CHIPS.map((chip) => {
                    const Icon = chip.icon;
                    return (
                      <div className="hvid-art-chip home-bubble" key={chip.label}>
                        <Icon size={20} weight="duotone" />
                        <span>{chip.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {posterStyle && <span className="hvid-scrim" aria-hidden="true" />}

            {hasSource ? (
              <button
                type="button"
                className="hvid-play"
                onClick={() => setPlaying(true)}
                aria-label={`הפעלת הסרטון: ${HOME_VIDEO.TITLE}`}
              >
                <span className="hvid-play-btn hvid-play-btn--pulse">
                  <Play size={30} weight="fill" />
                </span>
                <span className="hvid-play-label">
                  {HOME_VIDEO.TITLE}
                  {HOME_VIDEO.DURATION && <span className="hvid-play-dur">{HOME_VIDEO.DURATION}</span>}
                </span>
              </button>
            ) : (
              <div className="hvid-play hvid-play--static">
                <span className="hvid-play-btn">
                  <Play size={30} weight="fill" />
                </span>
                <span className="hvid-play-label">{HOME_VIDEO.TITLE}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {showOverlay && <MobileOverlay onClose={stop}>{renderPlayer(true)}</MobileOverlay>}
    </div>
  );
}

export default HomeVideo;
